import { useCallback } from 'react';
import { analyticsService } from '../services/analyticsService';

/**
 * React Hook for Analytics
 * Provides easy-to-use analytics tracking for components
 */
export function useAnalytics() {
  /**
   * Track a custom event
   */
  const trackEvent = useCallback((name: string, properties?: Record<string, any>) => {
    analyticsService.track({ name, properties });
  }, []);

  /**
   * Track a page view
   */
  const trackPageView = useCallback((pageName: string, additionalProperties?: Record<string, any>) => {
    analyticsService.track({
      name: 'page_viewed',
      properties: {
        page_name: pageName,
        path: window.location.pathname,
        referrer: document.referrer,
        ...additionalProperties
      }
    });
  }, []);

  /**
   * Track modal interaction
   */
  const trackModal = useCallback((
    action: 'opened' | 'closed',
    modalType: string,
    additionalProperties?: Record<string, any>
  ) => {
    analyticsService.track({
      name: `modal_${action}`,
      properties: {
        modal_type: modalType,
        ...additionalProperties
      }
    });
  }, []);

  /**
   * Track button click
   */
  const trackClick = useCallback((
    elementName: string,
    additionalProperties?: Record<string, any>
  ) => {
    analyticsService.track({
      name: 'button_clicked',
      properties: {
        element_name: elementName,
        ...additionalProperties
      }
    });
  }, []);

  /**
   * Track error
   */
  const trackError = useCallback((
    errorType: string,
    errorMessage: string,
    additionalProperties?: Record<string, any>
  ) => {
    analyticsService.track({
      name: 'error_occurred',
      properties: {
        error_type: errorType,
        error_message: errorMessage,
        ...additionalProperties
      }
    });
  }, []);

  /**
   * Track timing event (e.g., how long something took)
   */
  const trackTiming = useCallback((
    category: string,
    variable: string,
    timeMs: number,
    additionalProperties?: Record<string, any>
  ) => {
    analyticsService.track({
      name: 'timing_measured',
      properties: {
        category,
        variable,
        time_ms: timeMs,
        ...additionalProperties
      }
    });
  }, []);

  /**
   * Set user properties
   */
  const setUserProperties = useCallback((properties: Record<string, any>) => {
    analyticsService.setUserProperties(properties);
  }, []);

  /**
   * Increment a user property
   */
  const incrementUserProperty = useCallback((property: string, value: number = 1) => {
    analyticsService.incrementUserProperty(property, value);
  }, []);

  return {
    trackEvent,
    trackPageView,
    trackModal,
    trackClick,
    trackError,
    trackTiming,
    setUserProperties,
    incrementUserProperty
  };
}

export default useAnalytics;


